/**
 * Resolve which factory + config a market belongs to.
 *
 * Parameters change across deploys, and children from old `.sol` files keep
 * living next to new ones. Never assume `CONTRACT_ADDRESSES` is the factory
 * that created a given market.
 */
import { getAddress, isAddress, zeroAddress } from 'viem';
import {
  CONTRACT_ADDRESSES,
  PROTOCOL_DEPLOYMENTS,
  type ProtocolDeployment,
} from '../config/contracts';

export type DeploymentSource = 'on-chain' | 'db' | 'registry' | 'current';

export type ResolvedDeployment = {
  factory: `0x${string}`;
  config: `0x${string}`;
  source: DeploymentSource;
};

const ZERO = zeroAddress.toLowerCase();

export function normalizeHexAddress(addr: string | null | undefined): `0x${string}` | null {
  if (!addr || typeof addr !== 'string') return null;
  const trimmed = addr.trim();
  if (!isAddress(trimmed)) return null;
  try {
    const checksummed = getAddress(trimmed);
    if (checksummed.toLowerCase() === ZERO) return null;
    return checksummed;
  } catch {
    return null;
  }
}

export function allFactoryAddresses(): `0x${string}`[] {
  const seen = new Set<string>();
  const out: `0x${string}`[] = [];
  for (const d of PROTOCOL_DEPLOYMENTS) {
    const f = normalizeHexAddress(d.factory);
    if (!f) continue;
    const key = f.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f);
  }
  const current = normalizeHexAddress(CONTRACT_ADDRESSES.AAFactory);
  if (current && !seen.has(current.toLowerCase())) out.unshift(current);
  return out;
}

export function allConfigAddresses(): `0x${string}`[] {
  const seen = new Set<string>();
  const out: `0x${string}`[] = [];
  for (const d of PROTOCOL_DEPLOYMENTS) {
    const c = normalizeHexAddress(d.config);
    if (!c) continue;
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

export function deploymentForFactory(factory: string | null | undefined): ProtocolDeployment | null {
  const f = normalizeHexAddress(factory);
  if (!f) return null;
  const key = f.toLowerCase();
  return PROTOCOL_DEPLOYMENTS.find((d) => d.factory.toLowerCase() === key) ?? null;
}

export function configAddressForFactory(factory: string | null | undefined): `0x${string}` | null {
  const d = deploymentForFactory(factory);
  return d ? d.config : null;
}

export function isCurrentFactory(factory: string | null | undefined): boolean {
  const f = normalizeHexAddress(factory);
  if (!f) return false;
  return f.toLowerCase() === CONTRACT_ADDRESSES.AAFactory.toLowerCase();
}

export function oldestDeploymentBlock(): number | null {
  let min: number | null = null;
  for (const d of PROTOCOL_DEPLOYMENTS) {
    if (d.fromBlock == null || !Number.isFinite(d.fromBlock) || d.fromBlock < 0) continue;
    if (min == null || d.fromBlock < min) min = d.fromBlock;
  }
  return min;
}

/**
 * Pick factory + config for a market.
 *
 * 1. On-chain `factory()` / `config()` (new children always have these)
 * 2. Indexer `markets.factory_address` + registry config (or on-chain config)
 * 3. Registry match on either address
 * 4. Current `CONTRACT_ADDRESSES` (new launches / unknown)
 */
export function resolveDeployment(args: {
  onChainFactory?: string | null;
  onChainConfig?: string | null;
  dbFactory?: string | null;
}): ResolvedDeployment {
  const onChainFactory = normalizeHexAddress(args.onChainFactory);
  const onChainConfig = normalizeHexAddress(args.onChainConfig);
  const dbFactory = normalizeHexAddress(args.dbFactory);

  if (onChainFactory) {
    return {
      factory: onChainFactory,
      config:
        onChainConfig ??
        configAddressForFactory(onChainFactory) ??
        (CONTRACT_ADDRESSES.AAConfig as `0x${string}`),
      source: 'on-chain',
    };
  }

  if (dbFactory) {
    return {
      factory: dbFactory,
      config:
        onChainConfig ??
        configAddressForFactory(dbFactory) ??
        (CONTRACT_ADDRESSES.AAConfig as `0x${string}`),
      source: 'db',
    };
  }

  if (onChainConfig) {
    const matched = PROTOCOL_DEPLOYMENTS.find((d) => d.config.toLowerCase() === onChainConfig.toLowerCase());
    if (matched) {
      return { factory: matched.factory, config: onChainConfig, source: 'registry' };
    }
    return {
      factory: CONTRACT_ADDRESSES.AAFactory as `0x${string}`,
      config: onChainConfig,
      source: 'registry',
    };
  }

  return {
    factory: CONTRACT_ADDRESSES.AAFactory as `0x${string}`,
    config: CONTRACT_ADDRESSES.AAConfig as `0x${string}`,
    source: 'current',
  };
}

/**
 * Group key for epoch tabs / latest-round dedupe.
 * Same tweet on two factories is two lineages, not one card.
 * Rows with no factory still group by ref (legacy indexer rows).
 */
export function lineageGroupKey(
  factoryAddress: string | null | undefined,
  marketRef: string | null | undefined,
): string {
  const ref = (marketRef || '').trim();
  if (!ref) return '';
  const factory = normalizeHexAddress(factoryAddress);
  return factory ? `${factory.toLowerCase()}::${ref}` : ref;
}
